import React, { useState } from 'react';
import MetierSelector from '../components/MetierSelector';
import { Calculator, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MetiersDemo() {
  const [selectedMetiers, setSelectedMetiers] = useState({});
  const [chiffreAffaires, setChiffreAffaires] = useState(100000);
  const [calculResult, setCalculResult] = useState(null);
  const [formattedText, setFormattedText] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculerPrime = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/metiers/calculer-prime`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chiffre_affaires: chiffreAffaires,
          activites_selectionnees: selectedMetiers
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCalculResult(data.calcul);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleFormaterActivites = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/metiers/format-activites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedMetiers)
      });

      if (response.ok) {
        const data = await response.json();
        setFormattedText(data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedMetiers).reduce((total, metiers) => total + metiers.length, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🏗️ Démonstration Métiers COPPAM
          </h1>
          <p className="text-gray-600">
            Sélectionnez les activités professionnelles et testez le calcul de prime avec coefficients
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne gauche : Sélecteur */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Sélection des Activités
              </h2>
              <MetierSelector
                selectedMetiers={selectedMetiers}
                onChange={setSelectedMetiers}
                showCoefficients={true}
              />
            </div>
          </div>

          {/* Colonne droite : Calculs */}
          <div className="space-y-6">
            {/* Calcul de Prime */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Calculator className="w-5 h-5 mr-2 text-blue-600" />
                Calcul de Prime
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chiffre d'Affaires (€)
                  </label>
                  <input
                    type="number"
                    value={chiffreAffaires}
                    onChange={(e) => setChiffreAffaires(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="1000"
                  />
                </div>

                <button
                  onClick={handleCalculerPrime}
                  disabled={getSelectedCount() === 0 || loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Calcul...' : 'Calculer la Prime'}
                </button>

                {calculResult && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Prime de base :</span>
                      <span className="font-semibold">{calculResult.prime_base.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Coefficient moyen :</span>
                      <span className="font-semibold">{calculResult.coefficient_moyen.toFixed(3)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Nb activités :</span>
                      <span className="font-semibold">{calculResult.nombre_activites}</span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-blue-300 flex justify-between">
                      <span className="font-bold text-blue-900">Prime Nette :</span>
                      <span className="font-bold text-blue-900 text-lg">
                        {calculResult.prime_nette.toFixed(2)} €
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Formatage pour PDF */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-green-600" />
                Format PDF
              </h3>

              <button
                onClick={handleFormaterActivites}
                disabled={getSelectedCount() === 0 || loading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Formatage...' : 'Générer le Texte'}
              </button>

              {formattedText && (
                <div className="mt-4">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    Format Simple :
                  </div>
                  <pre className="p-3 bg-gray-50 border border-gray-200 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                    {formattedText.format_simple}
                  </pre>
                </div>
              )}
            </div>

            {/* Résumé */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-md p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">📊 Résumé</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Activités sélectionnées :</span>
                  <span className="font-semibold text-blue-900">{getSelectedCount()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Catégories :</span>
                  <span className="font-semibold text-blue-900">
                    {Object.keys(selectedMetiers).filter(k => selectedMetiers[k].length > 0).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
